import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Pages
import HomePage from "@/pages/HomePage";
import VortexPage from "@/pages/VortexPage";
import GamesPage from "@/pages/GamesPage";
import VideosPage from "@/pages/VideosPage";
import ForumPage from "@/pages/ForumPage";
import SubforumPage from "@/pages/SubforumPage";
import ThreadPage from "@/pages/ThreadPage";
import ProfilePage from "@/pages/ProfilePage";
import UploadPage from "@/pages/UploadPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import AuthCallback from "@/pages/AuthCallback";

// Components
import { Navbar } from "@/components/Navbar";
import { TheaterOverlay } from "@/components/TheaterOverlay";
import { AuthProvider, useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <div className="text-center">
          <div className="ws-logo mb-4">
            <span className="ws-logo-war">WAR</span>
            <span className="ws-logo-syndicate">SYNDICATE</span>
          </div>
          <p className="text-[#666] mono text-sm">LOADING...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// App Router with session_id detection
function AppRouter() {
  const location = useLocation();
  const [theaterContent, setTheaterContent] = useState(null);

  // CRITICAL: Check URL fragment for session_id synchronously during render
  // This prevents race conditions by processing new session_id FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <div className="scanlines min-h-screen bg-[#050505]">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 py-6">
        <Routes>
          <Route path="/" element={<HomePage setTheaterContent={setTheaterContent} />} />
          <Route path="/vortex" element={<VortexPage setTheaterContent={setTheaterContent} />} />
          <Route path="/games" element={<GamesPage setTheaterContent={setTheaterContent} />} />
          <Route path="/videos" element={<VideosPage setTheaterContent={setTheaterContent} />} />
          <Route path="/bbs" element={<ForumPage />} />
          <Route path="/bbs/:subforumId" element={<SubforumPage />} />
          <Route path="/bbs/thread/:threadId" element={<ThreadPage />} />
          <Route path="/user/:userId" element={<ProfilePage setTheaterContent={setTheaterContent} />} />
          <Route path="/upload" element={
            <ProtectedRoute>
              <UploadPage />
            </ProtectedRoute>
          } />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
        </Routes>
      </main>
      
      {theaterContent && (
        <TheaterOverlay 
          content={theaterContent} 
          onClose={() => setTheaterContent(null)} 
        />
      )}
      
      <Toaster 
        position="bottom-right"
        toastOptions={{
          style: {
            background: '#0a0a0a',
            border: '1px solid #cc0000',
            color: '#e5e5e5',
            fontFamily: 'Verdana, sans-serif',
            fontSize: '11px'
          }
        }}
      />
    </div>
  );
}

function App() {
  // Seed forum data on first load
  useEffect(() => {
    fetch(`${API}/seed`, { method: 'POST' }).catch(() => {});
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
